import {
  Alert,
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import React, {
  type ChangeEvent,
  useCallback,
  useEffect,
  useState
} from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'

import { useGetK8sOptionsList } from 'components/services/create/target-selection/kubernetes/hooks/use-get-k8s-options-list'
import { ServiceKubernetesTargetingSingleSelection } from 'components/services/create/target-selection/kubernetes/ServiceKubernetesTargetingSingleSelection'
import { type K8sAutocompleteSearchOption } from 'components/services/create/target-selection/kubernetes/types'
import { MonitorUrlFieldInput } from 'components/status-checks/form/status-check/common/MonitorUrlFieldInput'
import { StatusCheckNameField } from 'components/status-checks/form/status-check/common/StatusCheckNameField'
import { TestDynatracePreFlightButton } from 'components/status-checks/form/status-check/dynatrace/TestDynatracePreFlightButton'
import { useGetExistingStatusCheckByMonitorUrl } from 'components/status-checks/hooks/use-get-existing-status-check-by-monitor-url'
import {
  findK8sAutocompleteSearchOptionFromK8sQuery,
  getK8sQueryFromK8sAutocompleteSearchOption,
  isDynatraceSelectorEntityIdType,
  isDynatraceSelectorK8sQueryType,
  isDynatraceSelectorProblemsUrlType
} from 'components/status-checks/status-checks-dynatrace-form-utils'
import { type StatusCheckFormValues } from 'components/status-checks/status-checks-form-utils'
import { getStatusChecksUrls } from 'components/status-checks/status-checks-urls-utils'
import { useStatusCheckDetailsContext } from 'context/status-checks/StatusCheckDetailsContext'
import { useGetKubernetesTargetsByOwnerId } from 'hooks/use-get-kubernetes-targets-by-owner-id'

export enum DynatraceEntitySelectionSource {
  ENTITY_ID = 'entity_id',
  K8S_TARGETING = 'k8s_targeting',
  PROBLEMS_URL = 'problems_url'
}

const fieldsToWatch = [
  'dynatraceSelector',
  'dynatraceSelector.type',
  'dynatraceSelector.k8sQuery',
  'externalIntegration',
  'monitorUrl',
  'observabilityTool'
] as const

export const DynatraceMonitorSelection: React.FC = () => {
  const { control, formState, getFieldState, resetField, setValue } =
    useFormContext<StatusCheckFormValues>()
  const { statusCheckName: defaultName } = formState.defaultValues ?? {}
  const { isTestStatusCheckDisabled, setIsStatusCheckConnectionValidated } =
    useStatusCheckDetailsContext()

  const [
    dynatraceSelector,
    dynatraceSelectorType,
    dynatraceSelectorK8sQuery,
    externalIntegration,
    monitorUrl,
    observabilityTool
  ] = useWatch({ control, name: fieldsToWatch })

  const statusChecksUrls = getStatusChecksUrls()
  const { id: existingStatusCheckId } =
    useGetExistingStatusCheckByMonitorUrl(observabilityTool, monitorUrl) ?? {}

  const { labelKeys: targetsByOwnerId } = useGetKubernetesTargetsByOwnerId()
  const options = useGetK8sOptionsList(targetsByOwnerId)

  /*
   * The selected K8s object holds a different structure than the form field value, so we manage
   * the selection state locally.
   */
  const [selectedObject, setSelectedObject] =
    useState<K8sAutocompleteSearchOption | null>(
      findK8sAutocompleteSearchOptionFromK8sQuery(
        options,
        dynatraceSelectorK8sQuery
      ) ?? null
    )

  /*
   * K8s Targeting data is stored within the form state as a field via 'dynatraceSelector.k8sQuery'.
   * This handler will update the field value when a new k8s object is selected (or deselected).
   */
  const handleK8sTargetChange = useCallback(
    (selectedObject: K8sAutocompleteSearchOption | null) => {
      const { isDirty: isNameFieldDirty } = getFieldState('statusCheckName')
      const canAutoPopulateName = !defaultName && !isNameFieldDirty

      setSelectedObject(selectedObject)

      // Targeting selector value might be 'cleared'.
      if (!selectedObject) {
        resetField('dynatraceSelector.k8sQuery')

        // Clear any auto-populated name if one has been set
        if (canAutoPopulateName) {
          setValue('statusCheckName', '')
        }

        return
      }

      setValue(
        'dynatraceSelector.k8sQuery',
        getK8sQueryFromK8sAutocompleteSearchOption(selectedObject) ?? undefined,
        { shouldDirty: true }
      )

      // Auto-populate the name if one does not already exist
      if (canAutoPopulateName) {
        setValue('statusCheckName', selectedObject.label)
      }
    },
    [defaultName, getFieldState, resetField, setValue]
  )

  /*
   * Enhanced Dynatrace Status Checks can be created via three methods:
   * 1. Selecting a K8s Targeting object
   * 2. Providing an Entity ID
   * 3. Providing a Problems URL
   *
   * These values are held in the form state as a field. Selecting a method will update the field value and
   * will reset any previous values to ensure the endpoint configuration validations are wiped.
   */
  const handleSelectorTypeChange = useCallback(
    (
      _event: ChangeEvent<HTMLInputElement>,
      value: DynatraceEntitySelectionSource
    ) => {
      const { dynatraceSelector: defaultDynatraceSelector } =
        (formState.defaultValues as StatusCheckFormValues) ?? {}
      const { entityId: defaultEntityId, k8sQuery: defaultK8sQuery } =
        defaultDynatraceSelector ?? {}

      // reset local state for k8s selected objects
      setSelectedObject(null)

      // reset interactive field states
      resetField('dynatraceSelector.entityId')
      resetField('dynatraceSelector.k8sQuery')
      resetField('monitorUrl')

      // reset our form values to their defaults when changing the selector type
      setValue('dynatraceSelector.type', value)

      // reset form values conditional to the entity selection source type
      !isDynatraceSelectorEntityIdType(dynatraceSelector) &&
        setValue('dynatraceSelector.entityId', defaultEntityId)
      isDynatraceSelectorK8sQueryType(dynatraceSelector) &&
        setValue('dynatraceSelector.k8sQuery', defaultK8sQuery)
    },
    [dynatraceSelector, formState.defaultValues, resetField, setValue]
  )

  // We stabilize these callbacks as they may be included in effect hook dependencies
  const handleDynatracePreFlightCheckFailure = useCallback(() => {
    setIsStatusCheckConnectionValidated(false)
  }, [setIsStatusCheckConnectionValidated])

  const handleDynatracePreFlightCheckSuccess = useCallback(() => {
    setIsStatusCheckConnectionValidated(true)
  }, [setIsStatusCheckConnectionValidated])

  /*
   * In the event k8sClient data is not ready by the time this component loads, this
   * effect hook will ensure that the default dynatraceSelector.k8sQuery value is set.
   */
  useEffect(() => {
    if (!dynatraceSelectorK8sQuery || !selectedObject) return

    setSelectedObject(
      findK8sAutocompleteSearchOptionFromK8sQuery(
        options,
        dynatraceSelectorK8sQuery
      ) ?? null
    )
  }, [dynatraceSelectorK8sQuery, options, selectedObject])

  return (
    <Stack spacing={4}>
      <StatusCheckNameField />
      <Stack spacing={2}>
        <Typography>
          What monitor or alert will help Gremlin determine the health of your
          Service?
        </Typography>
        <Alert severity="info">
          If you are using Kubernetes, Gremlin can look up if the entity has a
          match in Dynatrace. Otherwise, a Health Check can be created with an
          Entity ID or by entering a specific Problem URL.
        </Alert>
        <Controller
          control={control}
          name="dynatraceSelector.type"
          render={({ field }) => {
            return (
              <RadioGroup
                {...field}
                value={dynatraceSelectorType}
                onChange={handleSelectorTypeChange}
              >
                <FormControl>
                  <Stack spacing={1.5}>
                    <FormControlLabel
                      control={
                        <Radio
                          value={DynatraceEntitySelectionSource.K8S_TARGETING}
                        />
                      }
                      label="Select Kubernetes Object to look up in Dynatrace"
                    />
                    {dynatraceSelectorType ===
                      DynatraceEntitySelectionSource.K8S_TARGETING && (
                      <Box sx={{ pb: 2, pl: 4 }}>
                        <ServiceKubernetesTargetingSingleSelection
                          selectedObject={selectedObject}
                          onChange={handleK8sTargetChange}
                        />
                      </Box>
                    )}
                  </Stack>
                </FormControl>
                <FormControl>
                  <Stack spacing={0.5}>
                    <FormControlLabel
                      control={
                        <Radio
                          value={DynatraceEntitySelectionSource.ENTITY_ID}
                        />
                      }
                      label="Provide an Entity ID"
                    />
                    {dynatraceSelectorType ===
                      DynatraceEntitySelectionSource.ENTITY_ID && (
                      <Controller
                        control={control}
                        name="dynatraceSelector.entityId"
                        render={({ field, fieldState: { error } }) => {
                          return (
                            <Box sx={{ pb: 2, pl: 4, width: '100%' }}>
                              <TextField
                                {...field}
                                data-testid="status-check-dynatrace-entity-id-input"
                                error={Boolean(error)}
                                helperText={error?.message}
                                label="Entity ID"
                                sx={{ width: '100%' }}
                                value={field.value ?? ''}
                              />
                            </Box>
                          )
                        }}
                      />
                    )}
                  </Stack>
                </FormControl>
                <FormControl>
                  <Stack spacing={1}>
                    <FormControlLabel
                      control={
                        <Radio
                          value={DynatraceEntitySelectionSource.PROBLEMS_URL}
                        />
                      }
                      label="Provide a Problems URL"
                    />
                    {dynatraceSelectorType ===
                      DynatraceEntitySelectionSource.PROBLEMS_URL && (
                      <Controller
                        control={control}
                        name="monitorUrl"
                        render={({ field, fieldState }) => (
                          <Box sx={{ pl: 4 }}>
                            <MonitorUrlFieldInput
                              externalIntegration={externalIntegration}
                              field={field}
                              fieldState={fieldState}
                              {...(existingStatusCheckId && {
                                to: statusChecksUrls.edit(existingStatusCheckId)
                              })}
                            />
                          </Box>
                        )}
                      />
                    )}
                  </Stack>
                </FormControl>
              </RadioGroup>
            )
          }}
        />
        {/* Dynatrace Problems URL validation should use the default Test Connection action */}
        {!isDynatraceSelectorProblemsUrlType(dynatraceSelector) && (
          <TestDynatracePreFlightButton
            dynatraceSelector={dynatraceSelector}
            externalIntegration={externalIntegration}
            isDisabled={isTestStatusCheckDisabled}
            onError={handleDynatracePreFlightCheckFailure}
            // We still allow HC creation to proceed on a pre-flight failure
            onFailure={handleDynatracePreFlightCheckSuccess}
            onSuccess={handleDynatracePreFlightCheckSuccess}
          />
        )}
      </Stack>
    </Stack>
  )
}
